<template>
  <div class="w-full">
    <h1 class="text-xl text-center mb-6">
      {{ $t('mycountly.signup.create-an-account') }}
    </h1>
    <cly-request
      disable-loading-icon
      silent-error
      @success="onSuccess"
      @error="onError"
    >
      <template #default="request">
        <cly-form @enter="isSubmissionAllowed && request.submit(getSignupSubmission)">
          <template #default="v">
            <cly-form-step id="signupStep">
              <cly-form-field
                :rules="'required|max:64|min:3'"
                class="mt-2 flex flex-col"
                disable-form-wrapping
                :label="$t('mycountly.signup.first-name')"
              >
                <el-input
                  id="input__signup-firstName"
                  v-model="signup.firstName"
                  class="w-full mt-1"
                  :placeholder="$t('mycountly.signup.placeholder.enter-your-first-name')"
                />
              </cly-form-field>
              <cly-form-field
                :rules="'required|max:64|min:3'"
                class="mt-2 flex flex-col"
                disable-form-wrapping
                :label="$t('mycountly.signup.last-name')"
              >
                <el-input
                  id="input__signup-lastName"
                  v-model="signup.lastName"
                  class="w-full mt-1"
                  :placeholder="$t('mycountly.signup.placeholder.enter-your-last-name')"
                />
              </cly-form-field>
              <cly-form-field
                :rules="'required'"
                :label="$t('mycountly.signup.designation')"
              >
                <el-select
                  v-model="signup.designation"
                  class="w-full mt-1"
                  :placeholder="$t('mycountly.signup.placeholder.enter-your-designation')"
                >
                  <el-option
                    v-for="designation in userDesignations"
                    :key="designation.value"
                    :label="designation.label"
                    :value="designation.value"
                  />
                </el-select>
              </cly-form-field>
              <cly-form-field
                :password-validation="true"
                vid="password"
                :rules="'required|min:8|max:32|one_uppercase|one_lowercase|one_number|one_special'"
                :label="$t('mycountly.signup.password')"
              >
                <template #default="validation">
                  <el-input
                    id="input__signup-password"
                    v-model="signup.password"
                    type="password"
                    class="w-full mt-1"
                    :placeholder="$t('mycountly.signup.placeholder.enter-your-password')"
                    @blur="passwordFocused = false"
                    @focus="passwordFocused = true"
                  />
                  <password-validation
                    v-if="passwordFocused && validation.errors.length"
                    :failed="validation.failedRules"
                  />
                </template>
              </cly-form-field>
              <cly-form-field class="!py-1">
                <el-checkbox
                  id="input__signup-terms-and-conditions"
                  v-model="termsAndConditionsChecked"
                  data-test="signup-check-1"
                  class="w-full text-sm !font-normal el-checkbox--multiline"
                >
                  {{ $t('mycountly.dashboard.prelogin.i-agree-to-the') }}
                  <a
                    href="https://countly.com/legal/flex-terms"
                    target="_blank"
                    class="underline"
                  >{{ $t('mycountly.dashboard.prelogin.terms-of-service') }}</a>
                  {{ $t('common.and') }}
                  <a
                    href="https://countly.com/legal/flex-privacy"
                    target="_blank"
                    class="underline"
                  >{{ $t('mycountly.dashboard.prelogin.privacy-policy') }}</a>
                </el-checkbox>
              </cly-form-field>
              <cly-form-field class="!py-1">
                <el-checkbox
                  id="input__signup-marketing-consent"
                  v-model="marketingConsentChecked"
                  data-test="signup-check-2"
                  class="w-full text-sm !font-normal el-checkbox--multiline"
                >
                  {{ $t('mycountly.dashboard.prelogin.marketing-consent') }}
                </el-checkbox>
              </cly-form-field>
              <myc-big-button
                id="button__signup-submit"
                test-id="signup-submit"
                :disabled="request.pending || !v.isSubmissionAllowed || !areCheckboxesOK"
                type="success"
                class="w-full !mt-5"
                @clicked="isSubmissionAllowed && !request.pending && request.submit(getSignupSubmission)"
              >
                {{ $t('mycountly.signup.create-account') }}
              </myc-big-button>
            </cly-form-step>
          </template>
        </cly-form>
      </template>
    </cly-request>
  </div>
</template>

<script>
import { mapActions } from 'pinia';
import { UserDesignations } from '@/modules/user/IUser';
import { useAuthStore } from '@/stores';
import { PreloginNavigationMixin } from '@/utils/mixins/prelogin-navigation';
import { AnalyticsMixin } from '@/utils/mixins/analytics';
import ClyFormStep from '@/components/countly/lib-components/form/cly-form-step.vue';
import ClyForm from '@/components/countly/lib-components/form/cly-form.vue';
import ClyFormField from '@/components/countly/lib-components/form/cly-form-field.vue';
import MycBigButton from '@/components/common/myc-big-button.vue';
import PasswordValidation from './containers/password-validation.vue';

export default {
  name: 'CreateAccountView',
  components: {
    MycBigButton,
    ClyForm,
    ClyFormStep,
    ClyFormField,
    PasswordValidation,
  },
  mixins: [PreloginNavigationMixin, AnalyticsMixin],
  data() {
    return {
      invitationToken: '',
      termsAndConditionsChecked: false,
      marketingConsentChecked: false,
      passwordFocused: false,
      signup: {
        firstName: '',
        lastName: '',
        designation: '',
        password: '',
      },
      userDesignations: UserDesignations,
    };
  },
  computed: {
    areCheckboxesOK() {
      return this.termsAndConditionsChecked;
    },
    isSubmissionAllowed() {
      return this.signup.firstName && this.signup.lastName && this.signup.designation && this.signup.password && this.areCheckboxesOK;
    },
  },
  created() {
    this.tryResuming(this.$router);
    this.invitationToken = this.$route.query.token;
    this.verifyInviteToken(this.invitationToken);
  },
  mounted() {
    this.startTimedEvent('signup');
    this.trackEvent('signupBegin');
  },
  methods: {
    ...mapActions(useAuthStore, ['submitVerifyInviteToken', 'submitSignUpInvited']),
    getSignupSubmission() {
      return this.submitSignUpInvited({ token: this.invitationToken, ...this.signup, marketingConsent: this.marketingConsentChecked });
    },
    onSuccess(res) {
      this.$clyNotify({ message: this.$t('mycountly.signup.invitation-accepted'), sticky: false, type: 'success' });
      this.eventSucceed('signupUser', {
        FirstName: res.payload.firstName, LastName: res.payload.lastName, Email: res.payload.email, From: 'Invitation',
      });
      this.setUserProfile('user', {
        name: res.payload.firstName + res.payload.lastName,
        email: res.payload.email,
        organization: res.payload.company.name,
        custom: {
          TeamSize: res.payload.company.size,
          Role: res.payload.designation,
          SignupStage: 'PUBLIC',
          MFA: res.payload.mfaEnabled,
          MarketingConsent: this.marketingConsentChecked,
        },
      });
      this.$router.push('/account/signin');
    },
    onError() {
      this.eventFailed('signupUser');
      this.$clyNotify({ message: this.$t('mycountly.errors.something-went-wrong'), sticky: false, type: 'error' });
    },
    async verifyInviteToken(token) {
      const result = await this.submitVerifyInviteToken(token);
      // if server returns 200, this means this email has an account
      // redirect to signin page
      if (result.isOk()) {
        this.$router.push('/account/signin');
      // if server returns 400, this means invitation token is invalid
      } else if (result.error.code === 400) {
        this.$router.push('/account/signin');
        this.$clyNotify({ message: this.$t('mycountly.errors.invalid-invitation-token'), sticky: false, type: 'error' });
      }
      // else, it means token valid and user does not have account
      // let user create account
    },
  },
};
</script>
